import * as React from "react";
import Icon from "~/components/icons/icon";
import classNames from "classnames";
import { debounce } from "~/utils/function-utils";
import { CSSTransition } from "react-transition-group";
import { currentMediaQueries } from "~/utils/breakpoint-utils";
import AnimateHeight from "react-animate-height";
import { isTouchInput } from "~/utils/input-detection-utils";
import { IBaseLink } from "~/types/link";

export interface INavigationDropdownState {
    isOpen: boolean;
    offsetX: number;
}

class NavigationDropdown extends React.Component<IBaseLink, INavigationDropdownState> {
    private container: HTMLDivElement | null;
    public state: INavigationDropdownState = {
        isOpen: false,
        offsetX: 0
    };

    constructor(props: IBaseLink) {
        super(props);

        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.keyEventHandler = this.keyEventHandler.bind(this);
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
    }

    public componentDidMount() {
        document.addEventListener("click", this.handleClickOutside);
        document.addEventListener("keyup", this.keyEventHandler);
    }

    public componentWillUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
        document.removeEventListener("keyup", this.keyEventHandler);
    }

    private handleClickOutside(event: Event) {
        const domNode = this.container;
        if (!domNode || (event.target && !domNode.contains(event.target as Node))) {
            this.close();
        }
    }

    private keyEventHandler(event: KeyboardEvent) {
        if (event.key === "Escape") {
            this.close();
        }
    }

    private handleDropdownClick(event: React.MouseEvent<HTMLAnchorElement>) {
        if (isTouchInput() || currentMediaQueries.wolfMax) {
            event.preventDefault();
            event.stopPropagation();
            this.toggle();
        }
    }

    private handleMouseOver() {
        if (currentMediaQueries.wolfMax) {
            return;
        }
        this.open();
    }

    private handleMouseLeave() {
        if (currentMediaQueries.wolfMax) {
            return;
        }
        this.close();
    }

    private open() {
        this.setState({
            isOpen: true
        });
    }

    private close() {
        this.setState({
            isOpen: false
        });
    }

    private toggle() {
        this.setState({
            isOpen: !this.state.isOpen
        });
    }

    public render() {
        const rootClasses = classNames("navigation-dropdown", {
            "navigation-dropdown--isOpen": this.state.isOpen
        });

        return (
            <div
                className={rootClasses}
                ref={node => (this.container = node)}
                onMouseLeave={debounce(() => this.handleMouseLeave(), 200)}
            >
                <div className="navigation-dropdown__togglewrap">
                    <a
                        href={this.props.url}
                        className="navigation-primary-links__link navigation-dropdown__link reset-a"
                        target={this.props.target}
                        onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                            this.handleDropdownClick(event)
                        }
                        onMouseEnter={debounce(() => this.handleMouseOver(), 200)}
                        onKeyPress={e => {
                            if (e.key === " ") {
                                e.preventDefault();
                                this.toggle();
                            }
                        }}
                    >
                        <div className="navigation-primary-links__link-inner">
                            <span>{this.props.label}</span>
                            <div className="navigation-dropdown__indicator">
                                <Icon
                                    iconId={this.state.isOpen ? "icon_16_arrow-up" : "icon_16_arrow-down"}
                                />
                            </div>
                        </div>
                    </a>
                </div>
                <AnimateHeight height={this.state.isOpen ? "auto" : 0} duration={300}>
                    <CSSTransition
                        in={this.state.isOpen}
                        timeout={300}
                        classNames="navigation-dropdown__dropdown-wrapper"
                    >
                        <div className="navigation-dropdown__dropdown-wrapper">
                            <div className="navigation-dropdown__dropdown">{this.props.children}</div>
                        </div>
                    </CSSTransition>
                </AnimateHeight>
            </div>
        );
    }
}

export default NavigationDropdown;
